import { useCallback } from "react";
import { useLocalStorageState } from "../../../../hooks/useLocalStorageState";
import { withoutRequest, withRequest } from "../utils/moveRequests";

/** Stable identity so `useLocalStorageState`'s reader isn't a new function on every render. */
const NO_REQUESTS: string[] = [];

/**
 * Per-user, per-department, per-job: which move requests this manager has already sent.
 *
 * Job-scoped because the recommendations are — a request against a `7am-3pm` CNA card means nothing on
 * the LPN board.
 */
export const moveRequestsStorageKey = (userId: string, departmentId: string, jobId: string): string =>
	`hiring-move-requests-${userId}-${departmentId}-${jobId}`;

/**
 * The set of `{recommendationKey}::{nurseId}` requests, persisted so the `Requested` state survives a
 * reload — the request itself left the app as a broadcast, so the browser is the only thing that
 * remembers it was sent.
 *
 * `clearRequests` DELETES the key rather than writing an empty array back: an empty stored array is a
 * value that reads identically to "nothing stored" and never goes away, so "revert" would leave a
 * permanent artefact behind for every job the user ever opened.
 */
export function useMoveRequests(storageKey: string) {
	const [requested, setRequested, clearRequests] = useLocalStorageState<string[]>(storageKey, NO_REQUESTS);

	const addRequest = useCallback((recKey: string, nurseId: number) => setRequested((previous) => withRequest(previous, recKey, nurseId)), [setRequested]);

	/**
	 * Un-mark one request. State only — it un-says nothing, because the message has already left the app
	 * as a broadcast; it corrects the BOARD when a row is marked and shouldn't be.
	 */
	const removeRequest = useCallback((recKey: string, nurseId: number) => setRequested((previous) => withoutRequest(previous, recKey, nurseId)), [setRequested]);

	return { requested, addRequest, removeRequest, clearRequests };
}
